import {
  buildMessagesReportFetchKey,
  enumerateDays,
  formatMessagesReportBoundaryDate,
  socialNetworkTypeIdToChartKey,
} from "./messagesReport"

export { formatMessagesReportBoundaryDate, buildMessagesReportFetchKey } from "./messagesReport"

const ALERT_CHART_COLOR_VARS = [
  "--chart-5",
  "--chart-1",
  "--chart-7",
  "--chart-6",
  "--chart-2",
  "--chart-4",
]

/** Stable dashboard / PDF colors per alert category label. */
const ALERT_TYPE_COLOR_BY_LABEL = {
  crisis: "--chart-5",
  "general question": "--chart-1",
  "guest on property": "--chart-7",
  "internal escalation": "--chart-6",
  "negative message": "--chart-2",
  "reservation request": "--chart-4",
}

export function colorVarForAlertTypeLabel(label, fallbackIndex = 0) {
  const normalized = String(label || "")
    .trim()
    .toLowerCase()
  if (ALERT_TYPE_COLOR_BY_LABEL[normalized]) {
    return ALERT_TYPE_COLOR_BY_LABEL[normalized]
  }
  return ALERT_CHART_COLOR_VARS[fallbackIndex % ALERT_CHART_COLOR_VARS.length]
}

/** Normalize API date field (alertDate or legacy alertDay). */
export function alertRowDateKey(row) {
  const raw = row?.alertDate ?? row?.alertDay
  if (raw == null || raw === "") return null
  return String(raw).slice(0, 10)
}

/** Stable bar-chart key from API alertType label. */
export function alertTypeToChartKey(alertType) {
  const slug = String(alertType || "Unknown")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
  return slug || "unknown"
}

const ALERT_REPORTING_COLOR_VARS = [
  "--alert-reporting-1",
  "--alert-reporting-2",
  "--alert-reporting-3",
  "--alert-reporting-4",
  "--alert-reporting-5",
]

function reportingCategoryFromRow(row) {
  const candidates = [
    row?.alertCategory,
    row?.categoryName,
    row?.clientCategory,
    row?.alertCategoryType,
    row?.alert_category,
    row?.category_name,
    row?.client_category,
  ]
  for (const raw of candidates) {
    if (raw == null || raw === "") continue
    if (typeof raw === "object") {
      const name = String(raw.description ?? raw.name ?? raw.label ?? "").trim()
      if (name) return name
      continue
    }
    const name = String(raw).trim()
    if (name) return name
  }
  return null
}

function isDayInRange(dayKey, rangeDays) {
  if (!dayKey || !rangeDays?.length) return false
  return dayKey >= rangeDays[0] && dayKey <= rangeDays[rangeDays.length - 1]
}

function ensureDayBucket(map, dayKey) {
  if (!dayKey) return null
  if (!map.has(dayKey)) map.set(dayKey, {})
  return map.get(dayKey)
}

/** Last calendar day in the selected range (for period-summary API rows). */
function resolveRangeDays(startDate, endDate) {
  const rangeDays = startDate && endDate ? enumerateDays(startDate, endDate) : []
  const latestDay = rangeDays.length > 0 ? rangeDays[rangeDays.length - 1] : null
  return { rangeDays, latestDay }
}

/**
 * @param {Array<{
 *   alertDate?: string | null,
 *   alertDay?: string | null,
 *   alertCount?: number,
 *   previousAlertCount?: number,
 *   alertType?: string,
 *   socialNetworkTypeId?: number,
 * }>} rows
 * @param {{
 *   startDate?: Date,
 *   endDate?: Date,
 *   platformKeysFilter?: string[] | null,
 *   socialNetworkTypes?: Array<{ id?: number, description?: string, name?: string }>,
 * }} options
 */
export function buildAlertsChartsFromRows(rows, options = {}) {
  const safeRows = Array.isArray(rows) ? rows : []
  const { startDate, endDate, platformKeysFilter, socialNetworkTypes = [] } = options

  const totalsByType = new Map()
  const previousByType = new Map()
  const dailyByDate = new Map()
  const typesInOrder = []
  const reportingTotals = new Map()
  const reportingInOrder = []

  const seenTypes = new Set()
  const seenReporting = new Set()
  const { rangeDays, latestDay } = resolveRangeDays(startDate, endDate)

  for (const row of safeRows) {
    if (platformKeysFilter?.length) {
      const platformKey = socialNetworkTypeIdToChartKey(
        socialNetworkTypes,
        row?.socialNetworkTypeId,
      )
      if (!platformKey || !platformKeysFilter.includes(platformKey)) continue
    }

    const typeName = String(row?.alertType || "Unknown").trim() || "Unknown"
    const count = Number(row?.alertCount) || 0
    const previous = Number(row?.previousAlertCount) || 0
    const day = alertRowDateKey(row)
    const reportingName = reportingCategoryFromRow(row)

    if (!seenTypes.has(typeName)) {
      seenTypes.add(typeName)
      typesInOrder.push(typeName)
    }

    totalsByType.set(typeName, (totalsByType.get(typeName) || 0) + count)
    previousByType.set(typeName, (previousByType.get(typeName) || 0) + previous)

    if (reportingName) {
      if (!seenReporting.has(reportingName)) {
        seenReporting.add(reportingName)
        reportingInOrder.push(reportingName)
      }
      reportingTotals.set(reportingName, (reportingTotals.get(reportingName) || 0) + count)
    }

    const chartKey = alertTypeToChartKey(typeName)
    if (day && isDayInRange(day, rangeDays)) {
      const bucket = ensureDayBucket(dailyByDate, day)
      if (bucket) bucket[chartKey] = (bucket[chartKey] || 0) + count
    } else if (latestDay) {
      const latestBucket = ensureDayBucket(dailyByDate, latestDay)
      if (latestBucket) {
        latestBucket[chartKey] = (latestBucket[chartKey] || 0) + count
      }
    } else if (day) {
      const bucket = ensureDayBucket(dailyByDate, day)
      if (bucket) bucket[chartKey] = (bucket[chartKey] || 0) + count
    }
  }

  const alertCategorySeries = typesInOrder.map((label, index) => ({
    key: alertTypeToChartKey(label),
    label,
    colorVar: colorVarForAlertTypeLabel(label, index),
    stackId: "alerts",
  }))

  const alertCategoryPieSeries = typesInOrder.map((label, index) => ({
    key: label,
    label,
    colorVar: colorVarForAlertTypeLabel(label, index),
  }))

  const alertCategories = typesInOrder.map((name) => ({
    name,
    count: totalsByType.get(name) || 0,
    previous: previousByType.get(name) || 0,
  }))

  const totalAlerts = alertCategories.reduce((sum, item) => sum + item.count, 0)
  const previousTotalAlerts = alertCategories.reduce(
    (sum, item) => sum + item.previous,
    0,
  )

  const dayKeys =
    startDate && endDate
      ? rangeDays.length > 0
        ? rangeDays
        : enumerateDays(startDate, endDate)
      : [...dailyByDate.keys()].sort()

  const alertsChartData = dayKeys.map((date) => {
    const bucket = dailyByDate.get(date) || {}
    const point = { date }
    for (const series of alertCategorySeries) {
      point[series.key] = bucket[series.key] || 0
    }
    return point
  })

  const alertsSeries = []
  for (const [date, bucket] of dailyByDate.entries()) {
    for (const series of alertCategorySeries) {
      const value = bucket[series.key] || 0
      if (value > 0) {
        alertsSeries.push({
          date,
          category: series.key,
          value,
        })
      }
    }
  }

  const alertReportingCategories = reportingInOrder.map((name, index) => ({
    name,
    count: reportingTotals.get(name) || 0,
  }))

  const alertReportingCategoryPieSeries = reportingInOrder.map((label, index) => ({
    key: label,
    label,
    colorVar: ALERT_REPORTING_COLOR_VARS[index % ALERT_REPORTING_COLOR_VARS.length],
  }))

  const totalAlertReportingCategories = alertReportingCategories.reduce(
    (sum, item) => sum + item.count,
    0,
  )

  return {
    alertCategories,
    alertCategorySeries,
    alertCategoryPieSeries,
    alertReportingCategories,
    alertReportingCategoryPieSeries,
    alertsChartData,
    alertsSeries,
    totalAlerts,
    previousTotalAlerts,
    totalAlertReportingCategories,
  }
}
